import { Typography } from '@mui/material';
import xss from 'xss';
import { color } from '@/colors';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Tag } from '@/UI/Tag/Tag';
import { getHandleColor } from '@/components/Nodes/DynamicNode/HandlesUtils';
import { UnverifiedModelDisclaimer } from '@/components/Common/NodeDataCard/UnverifiedModelDisclaimer';
import { VerifiedSeal } from '@/components/Common/NodeDataCard/VerifiedSeal';
import { CreditsDisplay } from '@/components/Common/NodeDataCard/CreditsDisplay';
import { HandleType } from '@/enums/handle-type.enum';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';

type NodeDataCardProps = {
  displayName: string;
  description: string;
  price?: number;
  isVerified?: boolean;
  inputTypes?: HandleType[];
  outputTypes?: HandleType[];
  px?: number;
  pt?: number;
  termsUrls?: string[];
  privacyUrl?: string;
  shouldDisplayEmptyOutput?: boolean;
};

const NodeDataCard = (props: NodeDataCardProps) => {
  const { isVerified, termsUrls, privacyUrl } = props;
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const termsUrl = termsUrls?.[0] || '';
  const privacyPolicyUrl = privacyUrl || '';
  const additionalTermsLink = termsUrls?.[1] || '';
  const isDisplayDisclaimer = Boolean(
    isSubscriptionTypeIn([
      SubscriptionType.Free,
      SubscriptionType.Starter,
      SubscriptionType.Pro,
      SubscriptionType.Team,
    ]) &&
    !isVerified &&
    termsUrl &&
    privacyPolicyUrl,
  );

  return (
    <NodeDataCardComponent
      {...props}
      termsUrl={termsUrl}
      privacyPolicyUrl={privacyPolicyUrl}
      additionalTermsLink={additionalTermsLink}
      isDisplayDisclaimer={isDisplayDisclaimer}
    />
  );
};

export const NodeDataCardComponent = ({
  displayName,
  description,
  price,
  isVerified,
  termsUrl,
  privacyPolicyUrl,
  additionalTermsLink,
  inputTypes,
  outputTypes,
  isDisplayDisclaimer,
  px = 2,
  pt = 2,
  shouldDisplayEmptyOutput = false,
}: Omit<NodeDataCardProps, 'termsUrls' | 'privacyUrl'> & {
  isDisplayDisclaimer: boolean;
  termsUrl: string;
  privacyPolicyUrl: string;
  additionalTermsLink: string;
}) => {
  const isDisplayInput = Boolean(inputTypes?.length);
  const hasOutputs = Boolean(outputTypes?.length);
  const isDisplayOutput = hasOutputs || shouldDisplayEmptyOutput;
  const isDisplayInputOutput = isDisplayInput && isDisplayOutput;

  return (
    <FlexCol sx={{ ...(px !== undefined && { px }), ...(pt !== undefined && { pt }), pb: 2, gap: 1, width: '100%' }}>
      {isVerified && (
        <Flex sx={{ justifyContent: 'flex-end' }}>
          <VerifiedSeal iconSize={12} />
        </Flex>
      )}
      <Typography variant="body-std-md">{displayName}</Typography>
      <Typography
        variant="body-sm-rg"
        dangerouslySetInnerHTML={{ __html: xss(description || displayName) }}
        className="wea-disable-link-decoration"
        color={color.White80_T}
      />
      {isDisplayInputOutput && (
        <FlexCenVer sx={{ gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
          {inputTypes?.map((input) => (
            <Tag key={input} text={input} bgColor={getHandleColor(input)} textColor={color.Black92} />
          ))}
          {isDisplayOutput && (
            <>
              <Typography variant="body-xs-rg">→</Typography>
              {hasOutputs ? (
                <>
                  {outputTypes?.map((output) => (
                    <Tag key={output} text={output} bgColor={getHandleColor(output)} textColor={color.Black92} />
                  ))}
                </>
              ) : (
                <Tag text="Unknown" bgColor={color.DataType_Any} textColor={color.Black92} />
              )}
            </>
          )}
        </FlexCenVer>
      )}
      <CreditsDisplay price={price} />
      {isDisplayDisclaimer && (
        <UnverifiedModelDisclaimer
          termsUrl={termsUrl}
          privacyUrl={privacyPolicyUrl}
          additionalTermsLink={additionalTermsLink}
        />
      )}
    </FlexCol>
  );
};

export default NodeDataCard;
